/* eslint-disable max-lines */
import { getActiveLanguageCode, translate } from 'services/i18nService';
import { FILTER_CATEGORIES, CAMS } from 'enum/helpers';
import { GENDER } from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import {
  capitalize,
  checkFilterCategories,
  handleMetaIndexing,
} from 'utils/helpers';
import {
  checkGenderPreferenceValue,
  checkSexBasedOnGenderPreference,
  getTranslatedGender,
} from 'utils/gender';

import { getSEOConfig } from './helpers/seoConfig';
import { DIRECTORY_URL } from './Api';


export const setAlternateHref = (key, history) => {
  const activeLanguageCode = getActiveLanguageCode();
  const hreflang = document.querySelectorAll('link[rel="alternate"]');
  const pathnameSplitted = window.location.pathname.split('/');
  const secParam = pathnameSplitted[1];

  const seoConfig = getSEOConfig(key);

  hreflang.forEach((hl) => {
    const hreflangDomain = hl.href.toLowerCase().split('/', 3).join('/');
    let hrefLangPath = window.location.pathname;
    const langKey = hl.getAttribute('hreflang');
    if (seoConfig.link[langKey]) {
      hrefLangPath = hrefLangPath.replace(secParam, seoConfig.link[langKey]);
    } else {
      hrefLangPath = hrefLangPath.replace(secParam, seoConfig.link.en);
    }
    hl.setAttribute('href', `${hreflangDomain}${hrefLangPath}`);
  });

  let urlPath = location.pathname.split('/');
  if (urlPath.length > 1 && urlPath[1] !== '') {
    const newUrl = seoConfig.link[activeLanguageCode];
    urlPath[1] = newUrl;
    urlPath = urlPath.join('/');
    history.replace(`${urlPath}${location.search}`);
  }
};

const directories = [
  'ethnicities',
  'hairColors',
  'bodyTypes',
  'transBodyTypes',
  'bodyHairs',
  'sexPreferences',
  'genders',
  'showTypes',
];
const locales = ['languages', 'countries'];
const sharedFilters = [
  'sexPreferences',
  'ethnicities',
  'hairColors',
  'transBodyTypes',
  'bodyTypes',
  'bodyHairs',
  'genders',
  'showTypes',
];
// add all to this
const contextuals = [
  'breastSizes',
  'penisSizes',
  'penisTypes',
  'maleRoles',
  'transPenisSizes',
  'transBreastSizes',
  'transPenisTypes',
  'femaleBodyTypes',
  'maleBodyTypes',
  'femaleTypes',
  'maleTypes',
  'transTypes',
];

const genderSpecificAttributes = {
  female: 'breastSizes',
  male: ['penisSizes', 'penisTypes', 'maleRoles'],
  transgender: ['transPenisSizes', 'transBreastSizes', 'transPenisTypes'],
  shemale: ['transPenisSizes', 'transBreastSizes', 'transPenisTypes'],
  couple: '',
  all: '',
};
const genderSpecificBodyTypes = {
  female: 'femaleBodyTypes',
  male: 'maleBodyTypes',
  transgender: ['transBodyTypes', 'bodyTypes'],
  shemale: ['transBodyTypes', 'bodyTypes'],
  couple: '',
  all: '',
};
const genderClassification = {
  female: 'femaleTypes',
  male: 'maleTypes',
  transgender: 'transTypes',
  shemale: 'transTypes',
  couple: '',
  all: '',
};

const getMetaTitle = (genderPreference, directoryNames, isHiddenCouple) => {
  switch (genderPreference) {
    case GENDER.MALE:
      return directoryNames.replace(
        directoryNames.substring(directoryNames.lastIndexOf(',') + 1),
        ` ${translate('metatags.v2.cam.guys')}`
      );

    case GENDER.TRANSGENDER:
      return directoryNames.replace(
        directoryNames.substring(directoryNames.lastIndexOf(',') + 1),
        ` ${translate('awards.awards.category.shemale')}`
      );

    case GENDER.COUPLE:
      return directoryNames.replace(
        directoryNames.substring(directoryNames.lastIndexOf(',') + 1),
        ` ${translate('awards.awards.category.couple')}`
      );

    case CAMS:
      if (isHiddenCouple) {
        return directoryNames.replace(
          directoryNames.substring(directoryNames.lastIndexOf(',') + 1),
          ` ${translate('awards.awards.category.couple')}`
        );
      }

      return '';
    case GENDER.FEMALE:
      return directoryNames.replace(
        directoryNames.substring(directoryNames.lastIndexOf(',') + 1),
        ` ${translate('metatags.v2.cam.girls')}`
      );

    default:
      return directoryNames;
  }
};

const getDescription = (genderPreference, directoryNames, isHiddenCouple, state) => {
  let directoryFiltersGender = '';

  switch (genderPreference) {
    case GENDER.MALE:
      directoryFiltersGender = translate('awards.awards.category.male');
      break;

    case GENDER.TRANSGENDER:
      directoryFiltersGender = translate('profile.profile.gender.transgender');
      break;

    case GENDER.COUPLE:
      directoryFiltersGender = translate('awards.awards.category.couple');
      break;

    case CAMS:
      if (isHiddenCouple) {
        directoryFiltersGender = translate('awards.awards.category.couple');
      }
      break;

    case GENDER.FEMALE:
      directoryFiltersGender = translate('awards.awards.category.female');
      break;

    default:
      directoryFiltersGender = '';
  }

  if (state.directoryAlias !== '') {
    const seoConfig = getSEOConfig(state.directoryAlias);

    return translate(seoConfig.meta);
  }

  return translate('metatags.v2.description', {
    0: directoryNames,
    1: directoryFiltersGender,
  });
};

const getTranslationsData = (filterType, v, hasPrivateShow, genderPreference) => {
  const translationsData = {
    languages: (lg) => translate(`languages.${lg}`),
    countries: (ct) => translate(`countries.${ct}`),
    ethnicities: (type) => {
      if (hasPrivateShow) {
        return undefined;
      }
      let latino = undefined;
      if (genderPreference === GENDER.FEMALE || genderPreference === GENDER.TRANSGENDER) {
        latino = capitalize(translate('categories.categories.Latina'));
      } else {
        latino = capitalize(translate('categories.categories.quicklinks.Latino'));
      }

      const ethnicityTypes = {
        arab: capitalize(translate('directory.directory.ethnicity.arab')),
        black: capitalize(translate('categories.categories.Ebony')),
        interracial: capitalize(translate('directory.ethnicity.mixed.race')),
        caucasian: capitalize(translate('directory.ethnicity.caucasian')),
        latino,
        default: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${v}`)),
      };

      return ethnicityTypes[type];
    },
    hairColors: (type) => {
      if (hasPrivateShow) {
        return undefined;
      }
      const hairColorTypes = {
        brown: capitalize(translate('categories.categories.Brunette')),
        red: capitalize(translate('categories.categories.Redhead')),
        other: capitalize(translate('profile.profile.hairColor.other.hair.color')),
        default: `${capitalize(translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${v}`))} ${
          !['bald', 'blonde'].includes(v) ? translate('directory.filter.hair') : ''
        }`,
      };

      return hairColorTypes[type];
    },
    bodyTypes: (type) => {
      if (hasPrivateShow) {
        return undefined;
      }
      const bodyTypes = {
        slimPetite: capitalize(translate('profile.profile.femaleBodyType.petite')),
        default: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${v.toLowerCase()}`)),
      };

      return bodyTypes[type];
    },
    transBodyTypes: (type) => {
      const typeModify = type.toLowerCase();
      if (hasPrivateShow) {
        return undefined;
      }

      return `${capitalize(translate(`profile.profile.transBodyType.${typeModify}`))} ${translate(
        'directory.seo.body'
      )}`;
    },
    bodyHairs: (type) => {
      if (hasPrivateShow) {
        return undefined;
      }

      return `${capitalize(translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type}`))} ${
        !['shaved', 'hairy'].find((e) => e === v) ? translate('profile.profile.bodyHair') : ''
      }`;
    },
    showTypes: (type) => {
      if (type === 'privateShow') {
        return capitalize(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type}`);
      }

      return '';
    },
    sexPreferences: (type) => capitalize(translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type}`)),
    femaleBodyTypes: (type) => {
      const bodyTypes = {
        petite: translate('profile.profile.femaleBodyType.petite'),
        default: translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${v.toLowerCase()}`),
      };

      return bodyTypes[type];
    },
    maleBodyTypes: (type) => translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type.toLowerCase()}`),
    femaleTypes: (type) => {
      const femaleTypes = {
        TEEN: translate('categories.categories.Teen'),
        BABE: translate('categories.categories.Babes'),
        MILF: translate('categories.categories.MILFs'),
        MATURE: translate('categories.categories.Mature'),
      };

      return femaleTypes[type] ? femaleTypes[type] : translate(`profile.profile.femaleType.${type.toLowerCase()}`);
    },
    breastSizes: (type) =>
      `${translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type.toLowerCase()}`)} ${translate(
        'profile.profile.breasts'
      )}`,
    transBreastSizes: (type) =>
      `${translate(`profile.profile.transBreastSize.${type.toLowerCase()}`)} ${translate('profile.profile.breasts')}`,
    penisSizes: (type) =>
      `${translate(`profile.profile.penisSize.${type.toLowerCase()}`)} ${translate('profile.profile.penis')}`,
    penisTypes: (type) => translate(`profile.profile.penisType.${type.toLowerCase()}`),
    maleTypes: (type) => translate(`profile.profile.maleType.${type.toLowerCase()}`),
    maleRoles: (type) => translate(`profile.profile.maleRole.${type.toLowerCase()}`),
    transTypes: (type) => translate(`profile.profile.transType.${type.toLowerCase()}`),
    transPenisSizes: (type) =>
      `${translate(`profile.profile.transPenisSize.${type.toLowerCase()}`)} ${translate('profile.profile.penis')}`,
    genders: (type) => capitalize(translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type}`)),
    quickFilter: () => capitalize(translate('account.galleries.sfw.new.tag')),
    default: (type) => translate(`profile.profile.${FILTER_CATEGORIES[filterType]}.${type.toLowerCase()}`),
  };

  return (
    (translationsData[filterType] && (translationsData[filterType](v) || translationsData[filterType]('default'))) ||
    translationsData.default(v)
  );
};


const getActiveFilterValue = (arr) => arr.filter((a) => a.get('active')).map((a) => a.get('value'));
const getActiveLocaleFilterCode = (arr) => arr.filter((a) => a.get('active')).map((a) => a.get('code'));

const getDirectoryNameList = (shared, allFilters, contextual, isHiddenCouple) => {
  const directoryName = [];

  if (shared) {
    // indexation rules
    // directoryName priority attribute is set by these rules:
    // {Orientation|Hair|Gender-Specific-Body|Body|Gender-Specific-Attributes|Ethnicity|Classification|Gender}
    // lower the number, higher the priority
    directories.forEach((dir) => {
      const val = getActiveFilterValue(shared.get(dir));
      if (val) {
        switch (dir) {
          case 'sexPreferences':
            [...val.values()].forEach((v) => {
              directoryName.push({
                priority: 1,
                type: 'sexPreferences',
                values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v}`)),
              });
            });
            break;

          case 'hairColors':
            [...val.values()].forEach((v) => {
              directoryName.push({
                priority: 2,
                type: 'hairColors',
                values: `${capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v}`))} ${translate(
                  'profile.profile.hairColor'
                )}`,
              });
            });
            break;

          case 'bodyTypes':
            [...val.values()].forEach((v) => {
              directoryName.push({
                priority: 3,
                type: 'bodyTypes',
                values: `${capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v}`))} ${translate(
                  'directory.seo.body'
                )}`,
              });
            });
            break;

          case 'transBodyTypes':
            [...val.values()].forEach((v) => {
              const vModify = v.toLowerCase();
              directoryName.push({
                priority: 3,
                type: 'transBodyTypes',
                values: `${capitalize(translate(`profile.profile.transBodyType.${vModify}`))} ${translate(
                  'directory.seo.body'
                )}`,
              });
            });
            break;

          case 'genders':
            [...val.values()].forEach((v) => {
              directoryName.push({
                priority: 8,
                type: 'genders',
                values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v}`)),
              });
            });
            break;

          default:
            [...val.values()].forEach((v) => {
              directoryName.push({
                priority: 5,
                type: dir,
                values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v}`)),
              });
            });
        }
      }
    });

    if (allFilters.get('hd')) {
      directoryName.push({
        priority: 7,
        type: 'hd',
        values: translate('directory.directory.filters.hd'),
      });
    }
    if (allFilters.get('kiiroo')) {
      directoryName.push({
        priority: 7,
        type: 'kiiroo',
        values: translate('directory.directory.filters.livetouch'),
      });
    }
    if (allFilters.get('source') === 'mobile') {
      directoryName.push({
        priority: 7,
        type: 'source',
        values: translate('directory.directory.filters.mobile'),
      });
    }
  }

  if (contextual) {
    contextual.forEach((contextualGender) => {
      contextualGender.mapKeys((dir) => {
        const val = getActiveFilterValue(contextualGender.get(dir));
        if (val) {
          switch (dir) {
            case 'femaleBodyTypes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 3,
                  type: 'femaleBodyTypes',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('directory.seo.body')}`,
                });
              });
              break;

            case 'breastSizes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 4,
                  type: 'breastSizes',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('profile.profile.breasts')}`,
                });
              });
              break;

            case 'transBreastSizes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 4,
                  type: 'transBreastSizes',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('profile.profile.breasts')}`,
                });
              });
              break;

            case 'transBodyTypes':
              [...val.values()].forEach((v) => {
                const vModify = v.toLowerCase();
                directoryName.push({
                  priority: 3,
                  type: 'transBodyTypes',
                  values: `${capitalize(translate(`profile.profile.transBodyType.${vModify}`))} ${translate(
                    'directory.seo.body'
                  )}`,
                });
              });
              break;

            case 'femaleTypes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 6,
                  type: 'femaleTypes',
                  values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)),
                });
              });
              break;

            case 'maleBodyTypes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 3,
                  type: 'maleBodyTypes',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('directory.seo.body')}`,
                });
              });
              break;

            case 'transPenisType':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 3,
                  type: 'transPenisType',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('profile.profile.penis')}`,
                });
              });
              break;

            case 'penisSizes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 4,
                  type: 'penisSizes',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('profile.profile.penis')}`,
                });
              });
              break;

            case 'transPenisSizes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 4,
                  type: 'transPenisSizes',
                  values: `${capitalize(
                    translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)
                  )} ${translate('profile.profile.penis')}`,
                });
              });
              break;

            case 'penisTypes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 4,
                  type: 'penisTypes',
                  values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)),
                });
              });
              break;

            case 'maleRoles':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 4,
                  type: 'penisTypes',
                  values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)),
                });
              });
              break;

            case 'maleTypes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 6,
                  type: 'maleTypes',
                  values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)),
                });
              });
              break;

            case 'transTypes':
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 6,
                  type: 'transTypes',
                  values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)),
                });
              });
              break;

            default:
              [...val.values()].forEach((v) => {
                directoryName.push({
                  priority: 5,
                  type: dir,
                  values: capitalize(translate(`profile.profile.${FILTER_CATEGORIES[dir]}.${v.toLowerCase()}`)),
                });
              });
          }
        }
      });
    });
  }

  if (isHiddenCouple) {
    directoryName.push({
      priority: 7,
      type: 'isHiddenCouple',
      values: capitalize(translate('awards.awards.category.couple')),
    });
  }

  return directoryName;
};

const getTitles = (
  genderPreference,
  shared,
  hasPrivateShow,
  allFiltersGroup,
  allFilters,
  contextual,
  locale,
  isHiddenCouple,
) => {
  const titles = [];

  const isTransgender = checkGenderPreferenceValue(genderPreference) === GENDER.TRANSGENDER;
  const englishGenderPreference = isTransgender ? GENDER.SHEMALE : checkGenderPreferenceValue(genderPreference);
  const attributes = genderSpecificAttributes[englishGenderPreference];
  const filtersOrderOfImportance = [
    ['languages', 'ethnicities', 'countries', 'sexPreferences'],
    [
      ...(attributes.constructor === Array ? attributes : [attributes]),
      'hairColors',
      genderSpecificBodyTypes[englishGenderPreference],
      'bodyTypes',
      'bodyHairs',
    ],
    [genderClassification[englishGenderPreference], 'genders'],
  ];

  filtersOrderOfImportance.forEach((filtersList) => {
    let foundObject = false;
    filtersList.forEach((filterType) => {
      const usedGroupObject = allFiltersGroup.filter((group) => group.stringData.includes(filterType))[0];
      let activeFiltersForType = '';

      if (
        contextual &&
        contextual.get(englishGenderPreference) &&
        contextual.get(englishGenderPreference).get(filterType) &&
        usedGroupObject !== undefined
      ) {
        activeFiltersForType = getActiveFilterValue(usedGroupObject.data.get(englishGenderPreference).get(filterType));
      } else if (locale && locale.get(filterType)) {
        activeFiltersForType = getActiveLocaleFilterCode(usedGroupObject.data.get(filterType));
      } else if (shared && shared.get(filterType) && usedGroupObject !== undefined) {
        activeFiltersForType = getActiveFilterValue(usedGroupObject.data.get(filterType));
      }

      if (activeFiltersForType && activeFiltersForType.size && !foundObject) {
        foundObject = true;
        [...activeFiltersForType.values()].forEach((v) => {
          titles.push(getTranslationsData(filterType, v, hasPrivateShow, genderPreference));
        });
      }
    });
  });

  if (shared) {
    if (allFilters.get('source') === 'mobile') {
      titles.push(translate('directory.directory.filters.mobile'));
    }
    if (allFilters.get('kiiroo')) {
      titles.push(translate('directory.directory.filters.livetouch'));
    }
    if (allFilters.get('c2cAllowed')) {
      titles.unshift('C2C');
    }
    if (allFilters.get('hd')) {
      titles.push(translate('directory.directory.filters.hd'));
    }
  }

  if (isHiddenCouple) {
    titles.push(capitalize(translate('awards.awards.category.couple')));
  }

  return titles;
};

const getH1key = (top, titles, match) => {
  const addition = top ? '.top' : '';
  if (titles.length === 1 && match.params.filter) {
    return `directory.v2.directory.trending.filtered.cams${addition}`;
  }

  const gender = titles[0]?.toLowerCase();
  const translatedGender = checkSexBasedOnGenderPreference(gender);
  switch (translatedGender) {
    case GENDER.FEMALE:
      return `directory.h1.title.female${addition}`;

    case GENDER.MALE:
      return `directory.h1.title.male${addition}`;

    case GENDER.COUPLE:
      return `directory.h1.title.couple${addition}`;

    case GENDER.TRANSGENDER:
      return `directory.h1.title.transgender${addition}`;

    case GENDER.ALL:
      return `directory.h1.title.all${addition}`;

    default:
      return `directory.h1.title.all${addition}`;
  }
};

// eslint-disable-next-line complexity
export const handleMetaDescriptionHelper = (
  props,
  state,
  changeTitleHeading,
  metaService,
  getGenderPref,
  changeTagHeading,
  changeTopTitle,
  changeTopTagTitle
) => {
  const {
    allFilters,
    user,
    match,
    location,
    history,
    directoryList,
  } = props;
  const { trendingTitle } = state;

  const genderPreference = match.params.genderPreference || user.genderPreference || GENDER.FEMALE;

  const shared = allFilters.get('shared');
  const locale = allFilters.get('locale');
  const contextual = allFilters.get('contextual');

  const allFiltersGroup = [
    {
      data: locale,
      stringData: locales,
    },
    {
      data: shared,
      stringData: sharedFilters,
    },
    {
      data: contextual,
      stringData: contextuals,
    },
  ];

  let metaContentSet = false;
  let hasPrivateShow = false;
  if (shared && shared.get('showTypes')) {
    hasPrivateShow = getActiveFilterValue(shared.get('showTypes')).find((e) => e === 'privateShow');
  }
  const hasLanguage =
    locale && locale.get('languages') ? getActiveLocaleFilterCode(locale.get('languages')).size > 0 : false;
  const hasCountry =
    locale && locale.get('countries') ? getActiveLocaleFilterCode(locale.get('countries')).size > 0 : false;

  // h1 rules
  // if couple has filters genderPreference will be 'cams' while 'couple' will be in in filters
  let isHiddenCouple = false;
  const paramFilter = match.params.filter && match.params.filter.split('/');
  const translatedCoupleOption = getTranslatedGender()[GENDER.COUPLE];

  if (
    genderPreference === translatedCoupleOption ||
    (genderPreference === CAMS && paramFilter && paramFilter[0] === translatedCoupleOption)
  ) {
    isHiddenCouple = true;
  }

  handleMetaIndexing(allFilters);

  const titles = getTitles(
    genderPreference,
    shared,
    hasPrivateShow,
    allFiltersGroup,
    allFilters,
    contextual,
    locale,
    isHiddenCouple,
  );
  const directoryName = getDirectoryNameList(shared, allFilters, contextual, isHiddenCouple);

  const sortDirectoryNames = (a, b) => {
    if (a.priority > b.priority) {
      return 1;
    }

    return -1;
  };

  const directoryNames = directoryName
    .sort(sortDirectoryNames)
    .map((d) => d.values)
    .join(', ');

  const isTagApplied = location.pathname.includes('tags');

  if (isTagApplied) {
    const h1Text = translate('directory.directory.trending.tags',
      { 0: `#${translate(`predefinedtags.${match.params.tag}`, {}, match.params.tag)}` });
    const topText = translate('directory.directory.trending.tags.top',
      { 0: `#${translate(`predefinedtags.${match.params.tag}`, {}, match.params.tag)}` });
    changeTagHeading(h1Text);
    changeTopTagTitle(topText);
  }

  let directoryTitles = undefined;
  if (
    allFilters &&
    checkFilterCategories(allFilters) &&
    location.pathname !== routeDefinitions.root
  ) {
    const key = (titles.length <= 1) ? getH1key(false, titles, match) : 'directory.v2.directory.filtered.cams';
    directoryTitles = translate(key, { 0: titles.join(' ') });
  } else {
    directoryTitles = translate('directory.v2.directory.trending.cams');
  }

  let topTitles = undefined;
  if (
    allFilters &&
    checkFilterCategories(allFilters) &&
    location.pathname !== routeDefinitions.root
  ) {
    const key = (titles.length <= 1) ? getH1key(true, titles, match) : 'directory.v2.directory.filtered.cams.top';
    topTitles = translate(key, { 0: titles.join(' ') });
  } else {
    topTitles = translate('directory.v2.directory.trending.cams.top');
  }

  if (trendingTitle !== directoryTitles || state.page > 1 || state.directoryAlias !== '') {
    const seoConfig = getSEOConfig(state.directoryAlias);
    if (state.directoryAlias !== '') {
      const title = translate(seoConfig.h1);
      changeTitleHeading(title);
      changeTopTitle(title);
    } else {
      const title =
        state.page > 1 && window.location.search !== '' ? `${directoryTitles} - ${state.page}` : directoryTitles;
      const topTitle = state.page > 1 && window.location.search !== '' ? `${topTitles} - ${state.page}` : topTitles;
      changeTitleHeading(title);
      changeTopTitle(topTitle);
    }
  }

  const metaTitle = getMetaTitle(genderPreference, directoryNames, isHiddenCouple);
  const description = getDescription(genderPreference, directoryNames, isHiddenCouple, state);

  const title = translate('metatags.v2.title', { 0: metaTitle });
  const paginationTranslation = translate('directory.page.page', { 0: state.page });

  const metaDescriptionTranslation = metaService.getMetaTranslation('description', match);
  const metaTitleTranslation = metaService.getMetaTranslation('title', match);
  const bodyMainTitleTranslation = metaService.getMetaTranslation('h1', match);

  const isBaseUrl = history.location.pathname === '/' && history.location.search === '';

  let finalTitle = undefined;
  if (isBaseUrl) {
    finalTitle = translate('directory.directory.title');
  } else if (metaTitleTranslation) {
    finalTitle = metaTitleTranslation;
  } else {
    finalTitle = title;
  }

  let finalDescription = undefined;
  if (isBaseUrl) {
    finalDescription = translate('directory.directory.description');
  } else if (metaDescriptionTranslation) {
    finalDescription = metaDescriptionTranslation;
  } else {
    finalDescription = description;
  }

  let genderPref = getGenderPref();
  if (genderPref === GENDER.TRANSGENDER) {
    genderPref = GENDER.SHEMALE;
  }

  const tagsDescription = match.params.tag && translate(
    `homepage.homepage.tags.description.${genderPref}`,
    { 0: capitalize(match.params.tag) }
  );

  const tagsTitle = match.params.tag && translate('metatags.v2.tags.title', {
    0: capitalize(getGenderPref()),
    1: capitalize(match.params.tag),
  });

  if (state.directoryAlias !== '') {
    const seoConfig = getSEOConfig(state.directoryAlias);

    finalTitle = translate(seoConfig.key);
    metaContentSet = true;
    metaService.setMetaContent(description, `${translate(seoConfig.meta)}`);

    window.history.replaceState(
      '',
      translate('directory.page.rooms.online.now', { 0: translate(seoConfig.h1) })
    );
  }

  if (bodyMainTitleTranslation) {
    if (isTagApplied) {
      changeTagHeading(bodyMainTitleTranslation);
    } else {
      changeTitleHeading(bodyMainTitleTranslation);
    }
  }

  const isTagsPage = location.pathname.includes('tags');

  if (!metaContentSet) {
    let preparedDescription = undefined;
    if (isTagsPage && !metaDescriptionTranslation) {
      preparedDescription = tagsDescription;
    } else {
      preparedDescription = finalDescription.replace(/ {2}/g, ' ');
    }

    const hasPagination = state.page > 1 && window.location.search !== '';
    const pagination = hasPagination ? ` ${paginationTranslation}` : '';

    let preparedTitle = undefined;
    if (isTagsPage && !metaTitleTranslation) {
      preparedTitle = `${tagsTitle}${pagination}`;
    } else {
      preparedTitle = `${finalTitle.trim()}${pagination}`;
    }

    metaService.setMetaContent(preparedDescription, preparedTitle);
  }

  if (
    (hasCountry || hasLanguage) &&
    match.params.filter?.split('/').length === 1 &&
    (!metaTitleTranslation || !metaDescriptionTranslation)
  ) {
    const activeLang = getActiveLocaleFilterCode(locale.get('languages')).first();
    const langName = activeLang ? translate(`languages.${activeLang}`) : '';

    const activeCount = getActiveLocaleFilterCode(locale.get('countries')).first();
    const countryName = activeCount ? translate(`countries.${activeCount}`) : '';

    const language = langName || countryName;

    const defaultMetaTitle = translate('metatags.v2.title', { 0: language });
    const defaultMetaDescription = translate('metatags.v2.description', {
      0: language,
      1: language,
    });

    if (!metaDescriptionTranslation) {
      metaService.setMetaDescription(defaultMetaDescription);
    }

    if (!metaTitleTranslation) {
      let paginationText = '';
      if (state.page > 1 && window.location.search !== '') {
        paginationText = paginationTranslation;
      }

      metaService.setMetaTitle(`${defaultMetaTitle} ${paginationText}`);
    }

    metaContentSet = true;
  }

  if (state.directoryAlias !== '') {
    const seoConfig = getSEOConfig(state.directoryAlias);
    metaService.setMetaContent(description, translate(seoConfig.h1));
  } else {
    metaService.setMetaUrl(directoryList);
  }
};

export const isSfw = (user) => {
  const userInfo = user.user.get('user');
  const sfw = userInfo.get('sfw');

  return sfw;
};

export const getDirectoryURL = (isUserSfw) => {
  if (isUserSfw) {
    return DIRECTORY_URL.concat('&sfw=true');
  }

  return DIRECTORY_URL;
};

export const genderFirst = (breadcrumbsList) => {
  if (breadcrumbsList.length > 1) {
    let genderIndex = 0;
    breadcrumbsList.forEach((el, ind) => {
      switch (el.urlPath) {
        case GENDER.MALE:
        case GENDER.FEMALE:
        case GENDER.TRANSGENDER:
        case GENDER.TRANS:
          genderIndex = ind;
          break;

        default:
      }
    });

    if (genderIndex > 0) {
      const tempGender = breadcrumbsList[genderIndex];
      breadcrumbsList[genderIndex] = breadcrumbsList[0];
      breadcrumbsList[0] = tempGender;
    }
  }

  return breadcrumbsList;
};
